//? for general info on stencil store, see https://github.com/ionic-team/stencil-store
//? for info on changing complex state, see https://stenciljs.com/docs/reactive-data#handling-arrays-and-objects

import {createStore} from "@stencil/store";
import {getRawAssets, getUserAssets, patchUserAssets, getCategories, patchCategories} from "./apiService";
import type {Stage, Asset, AssetPatch, Category, CategoryPatch} from "./apiService";
import {deepEquals} from "./utilities";

export type Categories = {
	isLoading: boolean;
	hasChanges: boolean;
	hasFailedGet: boolean;
	hasFailedPatch: boolean;
	data: Category[];
	update: (props: Partial<Omit<Categories, "update">>) => void;
};

export type Assets = {
	isLoading: boolean;
	hasChanges: boolean;
	hasFailedGet: boolean;
	hasFailedPatch: boolean;
	filter: string;
	data: Asset[];
	update: (props: Partial<Omit<Assets, "update">>) => void;
};

// helpers to prevent pseudo-changes (e.g. toggling a checkbox on->off->on) from triggering the .hasChanged properties
let categoriesDataRef: Category[] = [];
let assetsDataRef: Asset[] = [];

type InitArgs = {locale: string; stage: Stage; debug?: boolean};

const init = async ({locale, stage, debug = state.debugMode}: InitArgs) => {
	state.debugMode = debug;
	state.locale = locale;
	state.stage = stage;

	try {
		await Promise.all([fetchCategories(), fetchAssets()]);
	} catch (error) {
		console.error("An error occurred while initializing.", error);
		state.hasFailedInitializing = true;
		state.categories.update({isLoading: false, hasFailedGet: true});
		state.assets.update({isLoading: false, hasFailedGet: true});
	}
};

const fetchCategories = async () => {
	state.categories.update({isLoading: true, hasChanges: false, hasFailedGet: false});

	try {
		const {stage, locale} = state;
		const categories = await getCategories({stage, locale});

		// always sort system-generated category (if found) to the end
		const sortedCategories = categories.sort((a, b) => {
			if (a.name === "system-generated") return 1;
			if (b.name === "system-generated") return -1;
			return 0;
		});

		categoriesDataRef = structuredClone(sortedCategories);
		state.categories.update({hasFailedGet: false, data: sortedCategories});
	} catch (error) {
		console.error("An error occurred while fetching categories.", error);
		state.categories.update({hasFailedGet: true});
	} finally {
		state.categories.update({isLoading: false});
	}
};

const updateCategories = async () => {
	if (!state.categories.hasChanges) return;

	state.categories.update({hasFailedPatch: false});

	const categoriesDiff = state.categories.data.reduce((diff, sCategory) => {
		const refCategory = categoriesDataRef.find((rCategory) => rCategory.id === sCategory.id);

		const changedChannels = sCategory.channels.reduce(
			(acc, sChannel) => {
				const refChannel = refCategory.channels.find((rChannel) => rChannel.id === sChannel.id);
				if (refChannel.enabled !== sChannel.enabled) acc.push({id: sChannel.id, enabled: sChannel.enabled});
				return acc;
			},
			[] as CategoryPatch["channels"],
		);

		const changedSeverity = Object.entries(sCategory.severity).reduce(
			(acc, [key, value]) => {
				if (value !== refCategory.severity[key]) acc[key] = value;
				return acc;
			},
			{} as CategoryPatch["severity"],
		);

		if (changedChannels.length === 0 && Object.keys(changedSeverity).length === 0) return diff;

		diff.push({
			id: sCategory.id,
			...(changedChannels.length > 0 && {channels: changedChannels}),
			...(Object.keys(changedSeverity).length > 0 && {severity: changedSeverity}),
		});

		return diff;
	}, [] as CategoryPatch[]);

	try {
		const {stage} = state;
		await patchCategories({stage, categoriesDiff});

		categoriesDataRef = structuredClone(state.categories.data);
		state.categories.update({hasChanges: false, hasFailedPatch: false});
	} catch (error) {
		console.error("An error occurred while patching userCategories.", error);
		state.categories.update({hasFailedPatch: true});
	}
};

const resetCategories = () => {
	state.categories.update({isLoading: false, hasChanges: false, hasFailedGet: false, hasFailedPatch: false, data: categoriesDataRef});
};

const fetchAssets = async () => {
	state.assets.update({isLoading: true, hasChanges: false, hasFailedGet: false});

	try {
		const {stage} = state;
		const [rawAssets, userAssets] = await Promise.all([getRawAssets({stage}), getUserAssets({stage})]);

		const mergedAssets: Asset[] = rawAssets.map(({assetId: rawId, assetName: rawName}) => ({
			id: rawId,
			name: rawName,
			enabled: userAssets.find(({id: userId}) => userId === rawId)?.enabled ?? null,
		}));

		assetsDataRef = structuredClone(mergedAssets);
		state.assets.update({hasFailedGet: false, data: mergedAssets});
	} catch (error) {
		console.error("An error occurred while fetching assets.", error);
		state.assets.update({hasFailedGet: true});
	} finally {
		state.assets.update({isLoading: false});
	}
};

const updateAssets = async () => {
	if (!state.assets.hasChanges) return;

	state.assets.update({hasFailedPatch: false});

	const assetsDiff: AssetPatch[] = state.assets.data
		.filter((sAsset) => {
			const refAsset = assetsDataRef.find((rAsset) => rAsset.id === sAsset.id);
			return refAsset.enabled !== sAsset.enabled;
		})
		.map((sAsset) => ({id: sAsset.id, enabled: sAsset.enabled}));

	try {
		const {stage} = state;
		await patchUserAssets({stage, assetsDiff});

		assetsDataRef = structuredClone(state.assets.data);
		state.assets.update({hasChanges: false, hasFailedPatch: false});
	} catch (error) {
		console.error("An error occurred while patching userAssets.", error);
		state.assets.update({hasFailedPatch: true});
	}
};

const resetAssets = () => {
	state.assets.update({isLoading: false, hasChanges: false, hasFailedGet: false, hasFailedPatch: false, data: assetsDataRef});
};

const shouldUpdate = (newValue: unknown, oldValue: unknown, propChanged: string) => {
	if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return false;

	if (state.debugMode) {
		console.group("UserSettings Update");
		console.log("propChanged: ", propChanged);
		console.log("oldValue:    ", oldValue);
		console.log("newValue:    ", newValue);
		console.groupEnd();
	}

	return true;
};

type UserSettingsStore = {
	debugMode: boolean;
	token: string | null;
	locale: string | null;
	stage: Stage | null;

	hasFailedInitializing: boolean;

	categories: Categories;
	assets: Assets;

	init: (args: InitArgs) => Promise<void>;
	reset: () => void;

	updateCategories: () => Promise<void>;
	resetCategories: () => void;
	updateAssets: () => Promise<void>;
	resetAssets: () => void;
};

// extracted into separate functions to prevent circular dependencies in type inference
const _updateCategoriesState = (props: Partial<Omit<Categories, "update">>) => (state.categories = {...state.categories, ...props});
const _updateAssetsState = (props: Partial<Omit<Assets, "update">>) => (state.assets = {...state.assets, ...props});

const {
	state,
	onChange,
	// eslint-disable-next-line @typescript-eslint/unbound-method
	reset: storeReset,
} = createStore<UserSettingsStore>(
	{
		debugMode: false,
		token: null,
		locale: null,
		stage: null,

		hasFailedInitializing: false,

		categories: {
			isLoading: true,
			hasChanges: false,
			hasFailedGet: false,
			hasFailedPatch: false,
			data: [],
			update: _updateCategoriesState,
		},

		assets: {
			isLoading: true,
			hasChanges: false,
			hasFailedGet: false,
			hasFailedPatch: false,
			filter: "",
			data: [],
			update: _updateAssetsState,
		},

		init,
		reset: () => {
			storeReset();
		},

		updateCategories,
		resetCategories,
		updateAssets,
		resetAssets,
	},
	shouldUpdate,
);

// updates categories.hasChanges / assets.hasChanges only if categories.data / assets.data has (really) changed (ignores the initial fetch)
onChange("categories", () => state.categories.update({hasChanges: !deepEquals(state.categories.data, categoriesDataRef)}));

// onChange("categories", () => state.categories.update({hasChanges: JSON.stringify(state.categories.data) !== JSON.stringify(categoriesDataRef)}));
onChange("assets", () => state.assets.update({hasChanges: !deepEquals(state.assets.data, assetsDataRef)}));

export {state as userSettingsStore};
